/**
 * The button's variants, apart from the component.
 *
 * A router `<Link>` that should look like a button takes
 * `className={buttonVariants({ variant: "primary" })}` rather than an
 * `asChild` prop — one fewer dependency, and a link stays a link for a screen
 * reader. That is why this is exported at all.
 *
 * It is its OWN MODULE because a file exporting a component and a plain value
 * together loses fast refresh for the component: the bundler cannot tell
 * whether the value changed in a way the component depends on, so it reloads
 * the page instead of swapping the component.
 *
 * WHAT CHANGED, AND WHY
 *
 * `shrink-0` and `whitespace-nowrap` are the two that fix a real defect. A
 * button is a flex item wherever it sits, and without them it shrinks to fit
 * whatever space is left — which is how the query-detail composer ended up with
 * a 16px-wide "Send message" whose label wrapped one character per line. A
 * button should push its container wide, not fold.
 *
 * The focus ring is `ring-ring`, matching control.ts. It was `ring-primary`,
 * so a focused button and a focused input drew two different rings.
 *
 * `disabled` uses the design's own disabled tokens rather than `opacity-50`.
 * Half-opacity ink over a half-opacity fill lands at a contrast ratio nobody
 * chose, and it looks like a rendering fault rather than a state.
 */

import { cva } from "class-variance-authority"

export const buttonVariants = cva(
	[
		"inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
		"rounded-control text-field-label font-medium",
		"transition-[background-color,border-color,box-shadow,transform] duration-100",
		// A press is worth showing. Without it a tap on a phone gives no feedback
		// until the next screen paints, which on a slow connection reads as a
		// button that did not work and gets pressed again.
		"active:translate-y-px",
		"focus-visible:ring-ring focus-visible:ring-offset-canvas focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
		"disabled:bg-input-disabled disabled:text-disabled disabled:pointer-events-none disabled:border-transparent disabled:shadow-none",
	].join(" "),
	{
		variants: {
			variant: {
				// The one saturated thing on the screen is the next action — the
				// design's own rule. The shadow is what separates it from a filled
				// badge of the same colour.
				primary: "bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm",
				secondary:
					"border-border bg-surface text-foreground hover:bg-subtle hover:border-border-interactive border",
				ghost: "text-primary hover:bg-accent-soft",
				// The chrome's own affordance: a theme toggle, a close, a remove.
				//
				// NOT `ghost`, which is accented — `ghost` is a low-emphasis ACTION
				// and still reads as the brand colour, which is right for "Clear
				// filters" beside a submit and wrong for the sixteen little
				// square buttons that live in headers and file rows. Those want to
				// disappear until they are wanted.
				//
				// This exact recipe was already in the product, spelled out as a
				// 180-character className in seven places in apps/web and nine
				// more in apps/panel — a theme toggle, three rich-text controls, a
				// pane collapse, an attachment remove. Sixteen literal copies of
				// one look is sixteen places to miss when the hover changes.
				quiet: "text-muted hover:bg-subtle hover:text-foreground",
				danger: "bg-danger text-primary-foreground shadow-sm hover:opacity-90",
			},
			size: {
				// 44px is the design's `--size-tap-min` (US-00-005.14), so `md` is
				// the default and the only size a primary action should use on a
				// touch surface.
				md: "h-11 px-4",
				// 36px, for dense desktop rows — a table's inline action, a filter's
				// reset. Deliberately NOT the default: it is below the tap minimum,
				// and is correct only where a pointer is doing the work.
				sm: "text-meta h-9 px-3",
				// The one action a screen exists for, where it stands alone.
				lg: "text-body h-12 px-6",
				icon: "size-11",
				iconSm: "size-9",
				/**
				 * The floating action button: mobile's primary create, anchored to
				 * the thumb rather than to the top of the page.
				 *
				 * Round and 56px, which is the size a thumb actually hits — Material
				 * and HIG agree on this one. `FloatingAction` in floating-action.tsx
				 * positions it; this only describes the shape, so the same variant
				 * can be used for anything else that needs to float.
				 */
				fab: "rounded-circle size-14 shadow-lg",
			},
		},
		defaultVariants: { variant: "primary", size: "md" },
	},
)
